<template>
  <div v-if="shop" :class="$style.wrapper">
    <VTEmptyState
      v-if="isSectionsEmpty"
      :title="emptyState.title"
      :description="emptyState.description"
      :button-text="emptyState.buttonText"
      @empty-state-action="showAddSectionModal"
    />
    <draggable
      v-if="!shopsLoading"
      :disabled="isDraggingDisabled || !isWIPVersion"
      :list="sections"
      item-key="name"
      :class="$style.shopSectionWrapper"
      :animation="150"
      ghost-class="ghost"
      @dragend="handleReOrder"
    >
      <template #item="{ element }">
        <ShopSection
          :is-edit-allowed="isWIPVersion"
          :shop-id="shopId"
          :section="element"
          @delete="handleDelete"
          @edit="handleEdit"
          @append-shop-item="handleAddShopItem"
          @remove-shop-item="handleRemoveShopItem"
          @re-order-end="(event: SortUpdateEvent) => handleReOrderShopItems(event)"
        />
      </template>
    </draggable>
    <Skeleton v-if="shopsLoading" :class="$style.shopSectionPlaceholder" height="4rem" border-radius="16px"></Skeleton>
    <Skeleton v-if="shopsLoading" :class="$style.shopSectionPlaceholder" height="4rem" border-radius="16px"></Skeleton>
    <Button
      v-if="isWIPVersion && !isSectionsEmpty"
      icon="pi pi-plus"
      label="Add section"
      :class="$style.button"
      @click="showAddSectionModal"
    />
  </div>
  <Dialog
    v-model:visible="displayModal"
    :header="selectedSection ? 'Edit shop section' : 'Add shop section'"
    modal
    :style="{ width: '400px' }"
    dismissable-mask
    :draggable="false"
  >
    <ShopSectionForm
      :name="selectedSection?.name ?? ''"
      :shop-id="shop.id"
      @save-shop="handleSaveShopSection"
      @close="closeAddSectionModal"
    />
  </Dialog>
</template>

<script lang="ts" setup>
import VTEmptyState from "@/components/VTEmptyState/VTEmptyState.vue";
import ShopSectionForm from "@/modules/Store/components/ShopSectionForm.vue";
import useShopsManager from "@/modules/Store/hooks/useShopsManager";
import type { ShopSection as ShopSectionDTO, SortShopSectionPayload } from "@/modules/Store/shop.types";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import { useConfirm } from "primevue/useconfirm";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import draggable from "vuedraggable";
import { Shop } from "../shop.types";
import ShopSection from "./ShopSection.vue";
import type { ShopItemAddOrRemoveEvent, SortUpdateEvent } from "@/modules/Store/components/ShopSection.vue";
import Skeleton from "primevue/skeleton";

const confirm = useConfirm();
const route = useRoute();
const appId = route.params.gameId as string;
const versionId = route.params.versionId as string;
const props = defineProps<{
  shop: Shop;
  shopsLoading: boolean;
}>();
const sections = ref(JSON.parse(JSON.stringify(props.shop.sections)) as ShopSectionDTO[]);
const isSectionsEmpty = computed(() => !sections.value || sections.value.length === 0);
const displayModal = ref(false);
const isDraggingDisabled = ref(false);

const emptyState = {
  title: "No sections found",
  description: "You have no sections for the time being, to create a new section, use the button below",
  buttonText: "Add shop section",
};
const isWIPVersion = computed(() => versionId === "wip");
const selectedSection = ref<ShopSectionDTO | null>(null);

const { addShopSection, deleteShopSection, updateShopSection, sortShopSection, setActiveShop, sortShopItem } =
  useShopsManager(appId, versionId);
const showAddSectionModal = () => {
  displayModal.value = true;
};

const closeAddSectionModal = () => {
  if (selectedSection.value) {
    selectedSection.value = null;
  }
  displayModal.value = false;
};

const handleSaveShopSection = async (shopSectionName: string) => {
  const shopId = props.shop.id;
  if (!shopId) {
    return;
  }
  const shopSectionToUpdate = selectedSection.value;
  if (shopSectionToUpdate) {
    await updateShopSection(shopId, shopSectionToUpdate.id, {
      name: shopSectionName,
      rank: shopSectionToUpdate.rank,
    });
    selectedSection.value = null;
  } else {
    await addShopSection(shopId, {
      rank: props.shop.sections.length + 1,
      name: shopSectionName,
    });
  }
  closeAddSectionModal();
  setActiveShop(props.shop);
};

const shopId = computed(() => props.shop.id);

const handleDelete = async (sectionId: string) => {
  if (!shopId.value) {
    return;
  }
  const sectionToDelete = props.shop.sections.find((section) => section.id === sectionId);
  if (!sectionToDelete) {
    return;
  }
  confirm.require({
    message: `Are you sure you want to delete the section "${sectionToDelete.name}"?`,
    header: "Delete Confirmation",
    acceptLabel: "Yes",
    rejectLabel: "No",
    acceptClass: "p-button-danger",
    accept: async () => {
      await deleteShopSection(shopId.value, sectionId);
      setActiveShop(props.shop);
    },
    reject: () => {
      console.error("Delete operation cancelled.");
    },
  });
};

const handleEdit = async (sectionId: string) => {
  selectedSection.value = props.shop.sections.find((section) => section.id === sectionId) || null;
  showAddSectionModal();
};

const updateShopItemsDrag = ref<{
  shopItemId?: string;
  newShopSectionId?: string;
  newRank?: number;
  previousRank?: number;
  previousSectionId?: string;
}>({});
watch(
  () => props.shop.sections,
  (newSections) => {
    sections.value = [...newSections];
  }
);

const handleReOrder = async () => {
  if (!shopId.value) {
    return;
  }
  isDraggingDisabled.value = true;
  const sectionsUpdatePayload: SortShopSectionPayload = [];
  sections.value.forEach((section, index) => {
    sectionsUpdatePayload.push({
      id: section.id,
      rank: index + 1,
    });
  });
  await sortShopSection(shopId.value, sectionsUpdatePayload);
  isDraggingDisabled.value = false;
  setActiveShop(props.shop);
};

const handleAddShopItem = (event: ShopItemAddOrRemoveEvent) => {
  updateShopItemsDrag.value = {
    ...updateShopItemsDrag.value,
    newShopSectionId: event.sectionId,
    newRank: event.rank,
    shopItemId: event.shopItemId,
  };
};

const handleRemoveShopItem = (event: ShopItemAddOrRemoveEvent) => {
  updateShopItemsDrag.value = {
    ...updateShopItemsDrag.value,
    previousRank: event.rank,
    previousSectionId: event.sectionId,
    shopItemId: event.shopItemId,
  };
};
const handleReOrderShopItems = async (event: SortUpdateEvent) => {
  isDraggingDisabled.value = true;
  const { shopItemId, newShopSectionId, newRank, previousRank, previousSectionId } = event;
  await sortShopItem(props.shop.id, previousSectionId, shopItemId, {
    newRank,
    previousRank,
    newShopSectionId,
    previousShopSectionId: previousSectionId,
  });
  updateShopItemsDrag.value = {};
  isDraggingDisabled.value = false;
  setActiveShop(props.shop);
};

watch(
  () => updateShopItemsDrag.value,
  (updatedValue) => {
    if (
      updatedValue &&
      updatedValue.newRank &&
      updatedValue.previousRank &&
      updatedValue.shopItemId &&
      updatedValue.newShopSectionId &&
      updatedValue.previousSectionId
    ) {
      handleReOrderShopItems({
        newRank: updatedValue.newRank,
        previousRank: updatedValue.previousRank,
        shopItemId: updatedValue.shopItemId,
        newShopSectionId: updatedValue.newShopSectionId,
        previousSectionId: updatedValue.previousSectionId,
      });
    }
  }
);
</script>

<style module>
.wrapper {
  padding: 1rem 0 0 0;
  margin-top: 1rem;
}

.shopSectionWrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}
.shopSectionPlaceholder {
  margin-bottom: 1rem;
}
</style>

<style>
.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.list-group {
  min-height: 20px;
}

.list-group-item {
  cursor: move;
}

.list-group-item i {
  cursor: pointer;
}
</style>
